import React from "react";

/**
 * DancersBand — a panoramic strip of dancers that drifts slowly across the page.
 * The section height is driven by the image's natural aspect ratio so nothing
 * is ever cropped. Image is heavily optimised (WebP, ~62 KB; JPEG fallback ~107 KB)
 * and lazy-loaded so the homepage stays fast.
 */
const DancersBand = () => {
  // Source image is 3000x530 (cropped to remove dead space above raised hands)
  // → aspect ratio ≈ 5.66:1 → height ≈ 17.7% of width.
  // We expose this as the section's aspect-ratio so the band always matches the
  // photo exactly, regardless of viewport width — never cropped, never letter-boxed.
  // Default: grayscale; on hover the whole band fades back to full colour.
  return (
    <section
      className="group relative w-full overflow-hidden border-y border-gold/10"
      aria-hidden="true"
      data-testid="dancers-band"
      style={{ aspectRatio: "3000 / 530" }}
    >
      {/* Two copies side-by-side, each exactly 100vw wide → infinite seamless scroll */}
      <div
        className="absolute inset-y-0 left-0 flex transition-[filter] duration-700 ease-out"
        style={{
          width: "200vw",
          animation: "bandDrift 90s linear infinite",
          willChange: "transform, filter",
          filter: "grayscale(100%) contrast(1.05)",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.filter = "grayscale(0%) contrast(1)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = "grayscale(100%) contrast(1.05)"; }}
      >
        {[0, 1].map((i) => (
          <picture key={i} style={{ width: "100vw", height: "100%", flexShrink: 0 }}>
            <source srcSet="/images/dancers-band.webp" type="image/webp" />
            <img
              src="/images/dancers-band.jpg"
              alt=""
              loading="lazy"
              decoding="async"
              className="block select-none"
              style={{ width: "100vw", height: "100%", objectFit: "cover", objectPosition: "center" }}
              draggable={false}
            />
          </picture>
        ))}
      </div>

      {/* Edge fades so the band blends into the page on both sides */}
      <div
        className="absolute inset-y-0 left-0 w-32 md:w-48 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to right, #121212 0%, rgba(18,18,18,0.85) 40%, transparent 100%)" }}
      />
      <div
        className="absolute inset-y-0 right-0 w-32 md:w-48 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to left, #121212 0%, rgba(18,18,18,0.85) 40%, transparent 100%)" }}
      />

      {/* Subtle top + bottom darkening so the band feels grounded in the page */}
      <div
        className="absolute inset-x-0 top-0 h-8 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to bottom, rgba(18,18,18,0.7), transparent)" }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-8 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to top, rgba(18,18,18,0.7), transparent)" }}
      />

      {/* Barely-visible copper sheen on hover */}
      <div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none z-[1]"
        style={{ background: "radial-gradient(ellipse at center, rgba(184,115,51,0.08), transparent 70%)" }}
      />
    </section>
  );
};

export default DancersBand;
